import {ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {UserApiService} from '@app/services/api/user-api.service';
import {SwitchToFullModeDialogComponent} from '@app/dialogs/switch-to-full-mode-dialog/switch-to-full-mode-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ApiCachingService} from '@app/services/api/api-caching.service';
import {Router} from '@angular/router';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {FeatureFlagService} from '@app/services/feature-flag.service';
import {map, takeUntil} from 'rxjs/operators';
import {IntegrationsEnum} from '@app/interfaces/entities/integration';
import {PermissionEnum} from '@src/app/enums/permissions/PermissionEnum';
import {SideMenuEnum} from "@app/enums/SideMenuEnum";
import {UserProfile} from "@app/interfaces/entities/user-profile";
import {SidebarStateService} from "@app/services/util/sidebar-state.service";
import {EInvoiceBadgeService} from "@app/services/einvoice-badge.service";

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isMobile = false;

  public isDemo;
  public user: UserProfile;
  public AccountFlagEnum = AccountFeaturesEnum;
  public nlbLeasingRoute$: Observable<string>;
  public showFinancingSideMenu: boolean = false;

  protected readonly IntegrationsEnum = IntegrationsEnum;
  public PermissionEnum = PermissionEnum;

  private destroy$ = new Subject<void>();
  public isSidebarToggled: boolean = false;

  public readonly vehiclesSideBarItemLabel        = $localize`:@@vehiclesSideBarItem:Vozila`;
  public readonly advertisingSideBarItemLabel     = $localize`:@@advertisingSideBarItem:Oglaševanje`;
  public readonly clientsSideBarItemLabel         = $localize`:@@clientsSideBarItem:Imenik`;
  public readonly documentsSideBarItemLabel       = $localize`:@@documentsSideBarItem:Dokumenti`;
  public readonly assetsSideBarItemLabel          = $localize`:@@assetsSideBarItem:Blago & Storitve`;
  public readonly valuationSideBarItemLabel       = $localize`:@@valuationSideBarItem:Valuacija`;
  public readonly warrantiesSideBarItemLabel      = $localize`:@@warrantiesSideBarItem:Jamstva`;
  public readonly financingSideBarItemLabel       = $localize`:@@financingSideBarItem:Financiranja`;
  public readonly administrationToggleItemLabel = $localize`:@@administrationSideBarItem:Administracija`;
  public readonly businessUnitsToggleItemLabel = $localize`:@@boSideBarItem:Pregled poslovanja`;
  public administrationBadgeCount = 0;
  public administrationBadgeTooltip = '';

  constructor(
    private userApiService: UserApiService,
    private cacheApi: ApiCachingService,
    private matDialog: MatDialog,
    private router: Router,
    private featureFlagsService: FeatureFlagService,
    private sidebarState: SidebarStateService,
    private eInvoiceBadgeService: EInvoiceBadgeService,
  ) {
  }

  ngOnInit() {
    this.userApiService.getIsDemoStream()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isDemo = result;
      });

    this.userApiService.getUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.user = profile.data;
      });

    this.showFinancingSideMenu = this.router.url.startsWith('/admin/leasings/');
    if (this.isMobile && this.router.url.startsWith('/admin/administration/')) {
      this.toggleSideMenu(SideMenuEnum.Admininstration);
    } else if (this.isMobile && this.router.url.startsWith('/admin/business-overview/')) {
      this.toggleSideMenu(SideMenuEnum.BusinessOverview);
    }

    this.eInvoiceBadgeService.loadCounts();
    this.eInvoiceBadgeService.total$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.administrationBadgeCount = count;
        this.updateAdministrationBadgeTooltip();
      });
    this.eInvoiceBadgeService.incoming$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAdministrationBadgeTooltip());
    this.eInvoiceBadgeService.invoices$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAdministrationBadgeTooltip());
    this.eInvoiceBadgeService.advances$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAdministrationBadgeTooltip());

    this.nlbLeasingRoute$ = this.featureFlagsService.getFeatureFlags()
      .pipe(takeUntil(this.destroy$))
      .pipe(map((result) => {
        return result.nlbLeasingPreUX ? '/admin/leasings/drafts' : '/admin/leasings/ux';
      }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  openSwitchToFull() {
    const dialog = SwitchToFullModeDialogComponent.open(this.matDialog);
  }

  get isShrunk(): boolean {
    return this.sidebarState.isShrunk && !this.isMobile;
  }

  toggleSideMenu(menu: SideMenuEnum): void {
    this.sidebarState.toggleMenu(menu);
  }

  isSideMenuActive(menu: SideMenuEnum): boolean {
    return this.sidebarState.isMenuActive(menu);
  }

  isSideMenuHighlighted(menu: SideMenuEnum): boolean {
    return this.sidebarState.isMenuHighlighted(menu);
  }

  private updateAdministrationBadgeTooltip(): void {
    const parts: string[] = [];
    const incoming = this.eInvoiceBadgeService.incomingCount;
    const invoices = this.eInvoiceBadgeService.invoicesCount;
    const advances = this.eInvoiceBadgeService.advancesCount;
    if (incoming > 0) {
      parts.push($localize`:@@sidebar.badge.incoming:${incoming} vhodnih e-računov čaka na obdelavo`);
    }
    if (invoices > 0) {
      parts.push($localize`:@@sidebar.badge.outgoing:${invoices} izdanih e-računov čaka na vnos plačila`);
    }
    if (advances > 0) {
      parts.push($localize`:@@sidebar.badge.advances:${advances} izdanih avansnih e-računov čaka na vnos plačila`);
    }
    this.administrationBadgeTooltip = parts.join('\n');
  }

  protected readonly SideMenuEnum = SideMenuEnum;
}
